import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import {
  getMobileProfileMenuItems,
  getMobileProfileQuickNavItems,
} from '../ProfileNav/ProfileNav.constants';
import { isProfileNavItemActive } from '../ProfileNav/ProfileNav.helpers';
import * as S from './MobileProfileOverview.styled';
import { Drawer } from '@/components/Drawer/Drawer';
import {
  CloseIcon,
  ExitIcon,
  MoreHorizontalIcon,
} from '@/components/UI/Icons/Icons';
import { CopyButton } from '@/components/CopyButton';
import { formatNumber, formatPhoneNumber } from '@/utils/helpers';
import { useAuth } from '@/hooks/useAuth';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';
import { useCurrentUser } from '@/features/Profile/core/hooks';

type MobileProfileOverviewProps = {
  currentPath: string;
};

export function MobileProfileOverview({
  currentPath,
}: MobileProfileOverviewProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { loading, user } = useCurrentUser();
  const { logoutUser } = useAuth();
  const menuItems = getMobileProfileMenuItems(t);
  const quickNavItems = getMobileProfileQuickNavItems(t);

  function openMenu() {
    setIsMenuOpen(true);
  }

  function closeMenu() {
    setIsMenuOpen(false);
  }

  function handleLogout() {
    logoutUser();
  }

  return (
    <S.Container>
      <S.UserRow>
        <S.Avatar $avatarUrl={user.avatarUrl} />
        <S.UserInfo>
          {user.name && <S.UserName>{user.name}</S.UserName>}
          <S.UserMeta>
            {user.id && (
              <S.UserId>
                ID: {user.id}
                <CopyButton
                  value={user.id}
                  successText={t('pages.cabinet.profile_number_copied')}
                />
              </S.UserId>
            )}
            {user.phone && (
              <S.Phone> - {formatPhoneNumber(user.phone)}</S.Phone>
            )}
          </S.UserMeta>
        </S.UserInfo>
        <S.MenuButton
          type="button"
          onClick={openMenu}
          aria-label="Open profile menu"
        >
          <S.MenuButtonIconOutline>
            <MoreHorizontalIcon width={12} fill="currentColor" />
          </S.MenuButtonIconOutline>
        </S.MenuButton>
      </S.UserRow>

      <S.Balance>
        <div>
          <S.BalanceLabel>{t('pages.cabinet.private_account')}</S.BalanceLabel>
          {loading ? (
            <SkeletonLoader
              appearance="primary"
              width={80}
              height={22}
              borderRadius={7}
            />
          ) : (
            <S.BalanceValue>
              {formatNumber(user.walletBalance, 2)} ₼
            </S.BalanceValue>
          )}
        </div>
        <S.BalanceButton appearance="secondary" borderRadius={14}>
          {t('pages.cabinet.add')}
        </S.BalanceButton>
      </S.Balance>

      <S.QuickNav aria-label="Profile quick navigation">
        {quickNavItems.map(item => (
          <S.QuickNavItem key={item.href} href={item.href}>
            <S.QuickNavIcon>{item.icon}</S.QuickNavIcon>
            <S.QuickNavTitle>{item.title}</S.QuickNavTitle>
          </S.QuickNavItem>
        ))}
      </S.QuickNav>

      <Drawer
        isOpen={isMenuOpen}
        close={closeMenu}
        title={t('common.menu')}
        left={{ icon: <CloseIcon />, onClick: closeMenu }}
        background={theme.palette.primary.background}
        hasBottomMargin={false}
        hasBottomBorder={false}
      >
        <S.MenuContent>
          <S.MenuGrid aria-label="Profile menu">
            {menuItems.map(item => (
              <S.MenuItem
                key={item.href}
                href={item.href}
                $isActive={isProfileNavItemActive(currentPath, item.href)}
                onClick={closeMenu}
              >
                {item.icon}
                <S.MenuItemTitle>{item.title}</S.MenuItemTitle>
              </S.MenuItem>
            ))}
          </S.MenuGrid>

          <S.MenuDivider />

          <S.MenuLogoutButton type="button" onClick={handleLogout}>
            <ExitIcon />
            <S.ExitButtonTitle>{t('common.exit')}</S.ExitButtonTitle>
          </S.MenuLogoutButton>
        </S.MenuContent>
      </Drawer>
    </S.Container>
  );
}
